import { saveAs } from 'file-saver';
import kebabCase from 'lodash/kebabCase';
import { ref } from 'vue';
import { utils as xlsxUtils, write as xlsxWrite } from 'xlsx';
import State from '@/state/State';
import { captureCharts, captureKpis, captureOrphanKpis } from '@/utils/pdfChartCapture';
import { generateSubsectionPdf } from '@/utils/pdfSubsectionSheet';

export class NoExportableDataError extends Error {
  constructor() {
    super('No exportable data in the selected sections.');
    this.name = 'NoExportableDataError';
  }
}

export default function useSubsectionDownload() {
  const isGenerating = ref(false);

  function formatDate(date) {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  function formatDateSlug(date) {
    return date.toISOString().slice(0, 10);
  }

  function getEmployerName() {
    return State.state.company?.name || 'Employer';
  }

  function getEmployerSlug() {
    return kebabCase(getEmployerName());
  }

  /**
   * Canonical shape for additional sheet data. Children that contribute PDF data
   * should `defineExpose({ getPdfData: () => SheetExtras })` and parents wiring
   * them up via `<analytics-sub-page :get-sheet-config="...">` should pass that
   * function through unchanged. Adopters MUST NOT reshape between child and
   * composable.
   *
   * @typedef {Object} SheetExtras
   * @property {string} [label]               Override the descriptor's default label.
   * @property {string} [period]              Period text for the footer; defaults to the generated date.
   * @property {boolean} [isCompare]          Selects the compare-sheet layout; disables KPI capture.
   * @property {Object} [compareData]         `{ metric, divisions, period, generatedDate }` consumed by the compare layout.
   * @property {Array} [topKpis]              Pre-computed KPIs for the top strip (overrides DOM-captured KPIs).
   * @property {Array} [trendingBrands]       Trending Brands card data (Rewards pages).
   * @property {Array} [charts]               Pre-captured charts; when supplied, `captureCharts` is skipped.
   * @property {Object} [benchmarkPanel]      `{ title, subtitle, groups: [{ name, metrics: [...] }] }` for sections whose
   *                                          data is HTML bars (Compare's Industry Comparison, Impact's Workforce Impact).
   */

  /**
   * @typedef {Object} SheetDescriptor
   * @property {string} label                       Default section title shown in the header.
   * @property {() => HTMLElement} getContainerEl   Resolves the DOM element scraped for `data-pdf-*` markers.
   * @property {boolean} [inlineKpis=false]         Disables the top KPI strip; KPIs render next to their charts instead.
   * @property {() => SheetExtras} [getPdfData]     Resolves additional sheet data at download time.
   * @property {() => { headers: string[], rows: any[][] }} [getExportTable]
   *           Returns tabular data used for XLSX/CSV export. Required for non-PDF exports.
   */

  function buildSheet(descriptor, employer, generatedDate) {
    const extras = descriptor.getPdfData?.() || {};
    const containerEl = descriptor.getContainerEl();
    const inlineKpis = descriptor.inlineKpis || false;

    const charts = extras.charts || captureCharts(containerEl);
    const kpis = extras.isCompare || inlineKpis || extras.topKpis ? [] : captureKpis(containerEl);
    // Inline layout pairs KPIs with their charts; loose stat cards (no chart
    // in their group, or no group at all) would otherwise be dropped. Pick
    // them up so they still appear in the PDF as a small strip.
    const looseKpis = inlineKpis && !extras.isCompare && !extras.topKpis ? captureOrphanKpis(containerEl) : [];

    return {
      label: extras.label || descriptor.label,
      employer,
      period: extras.period || generatedDate,
      generatedDate,
      kpis,
      looseKpis,
      charts,
      isCompare: extras.isCompare || false,
      compareData: extras.compareData || null,
      benchmarkPanel: extras.benchmarkPanel || null,
      inlineKpis,
      topKpis: extras.topKpis || null,
      trendingBrands: extras.trendingBrands || null,
    };
  }

  function baseFilename(descriptors, now) {
    if (descriptors.length === 1) {
      return `stream-${kebabCase(descriptors[0].label)}-${getEmployerSlug()}-${formatDateSlug(now)}`;
    }
    return `stream-analytics-${getEmployerSlug()}-${formatDateSlug(now)}`;
  }

  async function downloadPdf(descriptors, now, pageExtras = null) {
    const employer = getEmployerName();
    const generatedDate = formatDate(now);
    const sheets = [];
    for (let i = 0; i < descriptors.length; i++) {
      let sheet = buildSheet(descriptors[i], employer, generatedDate);
      // Page-level extras (e.g. Summary snapshot KPIs, Compare's industry
      // benchmark panel) apply to the first sheet only so they don't repeat
      // across a multi-section PDF.
      if (i === 0 && pageExtras) {
        sheet = { ...sheet, ...pageExtras };
      }
      sheets.push(sheet);
      if (descriptors.length > 1) {
        // Yield between captures so the spinner can paint.
        await new Promise(r => setTimeout(r, 0));
      }
    }
    await generateSubsectionPdf(sheets, `${baseFilename(descriptors, now)}.pdf`);
  }

  function sheetNameFor(label) {
    // Excel sheet names: max 31 chars, no : \ / ? * [ ]
    return label.replace(/[:\\/?*[\]]/g, '').slice(0, 31) || 'Sheet';
  }

  function downloadXlsx(descriptors, now) {
    const book = xlsxUtils.book_new();
    book.Props = {
      Title: baseFilename(descriptors, now),
      Author: getEmployerName(),
      CreatedDate: now,
    };
    const usedNames = new Set();
    for (const descriptor of descriptors) {
      const table = descriptor.getExportTable?.();
      if (!table || !table.headers?.length) {
        continue;
      }
      let name = sheetNameFor(descriptor.label);
      let suffix = 2;
      while (usedNames.has(name)) {
        const trimmed = sheetNameFor(descriptor.label).slice(0, 28);
        name = `${trimmed} ${suffix++}`;
      }
      usedNames.add(name);
      const data = [table.headers, ...table.rows];
      const sheet = xlsxUtils.aoa_to_sheet(data);
      xlsxUtils.book_append_sheet(book, sheet, name);
    }
    if (book.SheetNames.length === 0) {
      throw new NoExportableDataError();
    }
    const out = xlsxWrite(book, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `${baseFilename(descriptors, now)}.xlsx`,
    );
  }

  function buildCsv(table) {
    const escape = value => `"${value == null ? '' : String(value).replace(/"/g, '""')}"`;
    const lines = [table.headers.map(escape).join(',')];
    for (const row of table.rows) {
      lines.push(row.map(escape).join(','));
    }
    return lines.join('\n');
  }

  async function downloadCsv(descriptors, now) {
    const tables = descriptors
      .map(d => ({ descriptor: d, table: d.getExportTable?.() }))
      .filter(t => t.table && t.table.headers?.length);
    if (!tables.length) {
      throw new NoExportableDataError();
    }

    if (tables.length === 1) {
      const csv = buildCsv(tables[0].table);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(
        blob,
        `${baseFilename(
          tables.map(t => t.descriptor),
          now,
        )}.csv`,
      );
      return;
    }

    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    const usedNames = new Set();
    for (const { descriptor, table } of tables) {
      let name = kebabCase(descriptor.label) || 'section';
      let suffix = 2;
      while (usedNames.has(name)) {
        name = `${kebabCase(descriptor.label)}-${suffix++}`;
      }
      usedNames.add(name);
      zip.file(`${name}.csv`, buildCsv(table));
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, `${baseFilename(descriptors, now)}.zip`);
  }

  /**
   * Download one or more subsection sheets in the chosen format.
   *
   * @param {SheetDescriptor[]} descriptors
   * @param {{ format?: 'pdf' | 'xlsx' | 'csv', pageExtras?: SheetExtras }} [options]
   *        `pageExtras` (PDF only) is shallow-merged into the first sheet so
   *        page-wide fields like `topKpis` or `benchmarkPanel` aren't repeated.
   * @returns {Promise<{ success: boolean, error?: Error }>}
   */
  async function downloadSheets(descriptors, options = {}) {
    if (isGenerating.value) {
      return { success: false };
    }
    const format = options.format || 'pdf';
    isGenerating.value = true;

    try {
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

      const now = new Date();
      if (format === 'pdf') {
        await downloadPdf(descriptors, now, options.pageExtras || null);
      } else if (format === 'xlsx') {
        downloadXlsx(descriptors, now);
      } else if (format === 'csv') {
        await downloadCsv(descriptors, now);
      } else {
        throw new Error(`Unknown download format: ${format}`);
      }
      return { success: true };
    } catch (err) {
      console.error(`[Download] Failed to generate ${format}:`, err);
      return { success: false, error: err };
    } finally {
      isGenerating.value = false;
    }
  }

  async function downloadSheet(descriptor, options) {
    return downloadSheets([descriptor], options);
  }

  return {
    isGenerating,
    downloadSheets,
    downloadSheet,
  };
}
